import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { CharMeter } from '../../components/CharMeter';
import { DraftNotice } from '../../components/DraftNotice';
import { planErrorMessage } from '../../components/plan/planErrors';
import { usePlanName } from '../../components/plan/usePlan';
import { QcStamp } from '../../components/QcStamp';
import { TokenListEditor } from '../../components/TokenListEditor';
import { ApiError } from '../../lib/apiClient';
import { brandIdentityDraftKey, clearDraft, restoreDraft } from '../../lib/draftStorage';
import { getErrorMessage } from '../../lib/errors';
import { formatTimestamp } from '../../lib/format';
import { useRefreshOn } from '../../lib/resourceRefresh';
import { IMAGE_ACCEPT_ATTRIBUTE, uploadImageToAssets, validateImageFile } from '../../lib/storage';
import { useDraft } from '../../lib/useDraft';
import type { BrandIdentityRead, BusinessRead } from '../../types/api';
import { AssetPreview } from './AssetPreview';
import { PictureStylePrefs } from './PictureStylePrefs';
import { assetsApi, fetchBrandIdentity, updateBrandIdentity } from './knowledgeBaseApi';
import { SectionShell } from './SectionShell';
import { updateBusiness } from '../onboarding/accountApi';
import { BRIEF_CAPS } from '../onboarding/brandBrief';
import { useAccount } from '../onboarding/useAccount';
import { useBrandCopy } from '../onboarding/useBrandCopy';

interface NamedKey {
  key: string;
  label: string;
  hint: string;
}

/** Colors the design agent consumes. Extra keys from the API are preserved on save. */
const COLOR_KEYS: readonly NamedKey[] = [
  { key: 'primary', label: 'Primary', hint: 'Headlines and fills' },
  { key: 'secondary', label: 'Secondary', hint: 'Supporting surfaces' },
  { key: 'accent', label: 'Accent', hint: 'Highlights only' },
];

const FONT_KEYS: readonly NamedKey[] = [
  { key: 'heading', label: 'Heading font', hint: 'Slide headlines' },
  { key: 'body', label: 'Body font', hint: 'Captions and slide copy' },
];

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** `brand_identity.picture_notes` cap, matching the column's CHECK. */
const PICTURE_NOTES_MAX = 500;

/** Share of the cap at which the notes counter starts warning, as the brief does. */
const NEAR_LIMIT_RATIO = 0.9;

function counterClassName(count: number, limit: number): string {
  if (count > limit) return 'layer-editor__count layer-editor__count--over';
  if (count >= limit * NEAR_LIMIT_RATIO) return 'layer-editor__count layer-editor__count--near';
  return 'layer-editor__count';
}

interface BrandIdentityForm {
  /** The business row's `value_proposition`, saved through `PUT /businesses/{id}`. */
  what_you_do: string;
  /** The business row's `target_audience`, same call. */
  who_its_for: string;
  tone: string;
  tagline: string;
  colors: Record<string, string>;
  fonts: Record<string, string>;
  never_say: string[];
  picture_styles: string[];
  avoid_picture_styles: string[];
  picture_notes: string;
}

const EMPTY_FORM: BrandIdentityForm = {
  what_you_do: '',
  who_its_for: '',
  tone: '',
  tagline: '',
  colors: {},
  fonts: {},
  never_say: [],
  picture_styles: [],
  avoid_picture_styles: [],
  picture_notes: '',
};

function toForm(
  identity: BrandIdentityRead | null,
  business: BusinessRead | null,
): BrandIdentityForm {
  const about = {
    what_you_do: business?.value_proposition ?? '',
    who_its_for: business?.target_audience ?? '',
  };
  if (identity === null) return { ...EMPTY_FORM, ...about };
  return {
    ...about,
    tone: identity.tone ?? '',
    tagline: identity.tagline ?? '',
    colors: { ...identity.colors },
    fonts: { ...identity.fonts },
    never_say: [...identity.never_say],
    // Read defensively: a server that predates migration 0047 answers without
    // them, and the form must still open.
    picture_styles: [...(identity.picture_styles ?? [])],
    avoid_picture_styles: [...(identity.avoid_picture_styles ?? [])],
    picture_notes: identity.picture_notes ?? '',
  };
}

/** Drops blank values so the API stores only real entries. */
function compact(map: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(map).filter(([, value]) => value.trim().length > 0));
}

/** Result of the last completed load, tagged with the business it belongs to. */
interface LoadedIdentity {
  businessId: string;
  /** Null when the singleton does not exist yet, or the load failed. */
  identity: BrandIdentityRead | null;
  error: string | null;
  /** Which read this was. A re-read remounts the editor on the fresh record. */
  nonce: number;
}

interface BrandIdentityEditorProps {
  businessId: string;
  loaded: BrandIdentityRead | null;
}

/**
 * The form itself, mounted only once the record has loaded — which is what lets
 * a saved draft be restored in a lazy initialiser instead of an effect.
 */
function BrandIdentityEditor({ businessId, loaded }: BrandIdentityEditorProps) {
  const copy = useBrandCopy();
  const planName = usePlanName();
  const { business, setBusiness } = useAccount();
  const isOwner = business?.role === 'owner';
  const draftKey = brandIdentityDraftKey(businessId);
  const [identity, setIdentity] = useState<BrandIdentityRead | null>(loaded);
  // The record as the server has it. It moves on every successful save, which
  // is what retires the draft.
  const pristine = toForm(identity, business);
  const [restored] = useState(() => restoreDraft(draftKey, pristine));
  const [form, setForm] = useState<BrandIdentityForm>(restored.value);
  const [restoredAt, setRestoredAt] = useState<string | null>(restored.savedAt);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  useDraft(draftKey, form, pristine);

  /** After a save: the record and the fields are the same thing again. */
  function applyIdentity(result: BrandIdentityRead, savedBusiness: BusinessRead | null) {
    setIdentity(result);
    setForm(toForm(result, savedBusiness));
  }

  /**
   * After a logo write: the record moved, but the fields did not. Leaving the
   * form alone keeps unsaved tone or tagline text on screen.
   */
  function applyLogo(result: BrandIdentityRead) {
    setIdentity(result);
  }

  /** This section has no cancel, so the notice carries the way out. */
  function discardDraft() {
    clearDraft(draftKey);
    setForm(pristine);
    setRestoredAt(null);
  }

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaveError(null);
    setSavedAt(null);

    const colors = compact(form.colors);
    const invalidColor = Object.entries(colors).find(([, value]) => !HEX_COLOR.test(value.trim()));
    if (invalidColor) {
      setSaveError(`${invalidColor[0]} color must be a 6-digit hex value such as #3A5A78.`);
      return;
    }

    setIsSaving(true);
    // Business first: What you do / Who it's for live on the business row,
    // which only the owner may write. Sent only when they changed.
    let savedBusiness = business;
    let businessError: string | null = null;
    const aboutChanged =
      form.what_you_do.trim() !== pristine.what_you_do.trim() ||
      form.who_its_for.trim() !== pristine.who_its_for.trim();
    if (isOwner && aboutChanged) {
      try {
        savedBusiness = await updateBusiness(businessId, {
          value_proposition: form.what_you_do.trim(),
          target_audience: form.who_its_for.trim(),
        });
        // The cached brand follows, as the settings form does it.
        setBusiness(savedBusiness);
      } catch (caught) {
        businessError = planErrorMessage(
          caught,
          'What you do and who it is for did not save. Try again.',
          { plan: planName },
        );
      }
    }

    try {
      const result = await updateBrandIdentity(businessId, {
        tone: form.tone.trim() || null,
        tagline: form.tagline.trim() || null,
        // `location` is deliberately absent: no screen edits it any more, and
        // the PUT is a partial upsert, so leaving it out leaves the stored
        // value alone — this form must not post a field it never showed.
        colors,
        fonts: compact(form.fonts),
        never_say: form.never_say,
        picture_styles: form.picture_styles,
        avoid_picture_styles: form.avoid_picture_styles,
        // The server stores "" as null too; trimming here keeps the two equal.
        picture_notes: form.picture_notes.trim() || null,
      });
      if (businessError === null) {
        applyIdentity(result, savedBusiness);
        // On file: the local copy is dead weight, and the notice about it is stale.
        clearDraft(draftKey);
        setRestoredAt(null);
        setSavedAt(result.updated_at);
      } else {
        // Keep the unsaved text on screen (and in the draft) for a retry.
        setIdentity(result);
        setSaveError(businessError);
      }
    } catch (caught) {
      const identityError = getErrorMessage(caught, 'Your brand details did not save. Try again.');
      setSaveError(businessError ? `${businessError} ${identityError}` : identityError);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleLogoChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const validationMessage = validateImageFile(file);
    if (validationMessage) {
      setLogoError(validationMessage);
      return;
    }

    setLogoError(null);
    setIsUploading(true);
    try {
      // Signed upload, then record the asset row, then point the brand at it.
      const storagePath = await uploadImageToAssets(businessId, file);
      await assetsApi.create(businessId, {
        storage_path: storagePath,
        kind: 'logo',
        tags: [],
        product_id: null,
      });
      const result = await updateBrandIdentity(businessId, { logo_path: storagePath });
      applyLogo(result);
    } catch (caught) {
      setLogoError(planErrorMessage(caught, 'Logo upload failed. Try again.', { plan: planName }));
    } finally {
      setIsUploading(false);
    }
  }

  async function handleLogoClear() {
    setLogoError(null);
    setIsUploading(true);
    try {
      const result = await updateBrandIdentity(businessId, { logo_path: null });
      applyLogo(result);
    } catch (caught) {
      setLogoError(getErrorMessage(caught, 'Could not clear the logo. Try again.'));
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <>
      <form className="form kb-form" onSubmit={(event) => void handleSave(event)} noValidate>
        {restoredAt ? (
          <DraftNotice
            savedAt={restoredAt}
            onDiscard={discardDraft}
            onDismiss={() => setRestoredAt(null)}
          />
        ) : null}

        <div className="kb-form__grid">
          <label className="field">
            <span className="field__label">{copy.records.identity.about.label}</span>
            <span className="field__hint field__hint--lead">
              {copy.records.identity.about.hint}
            </span>
            <textarea
              className="field__input"
              rows={3}
              maxLength={BRIEF_CAPS.value_proposition}
              placeholder={copy.records.identity.about.placeholder}
              value={form.what_you_do}
              disabled={!isOwner}
              onChange={(event) => setForm({ ...form, what_you_do: event.target.value })}
            />
          </label>
          <label className="field">
            <span className="field__label">{copy.records.identity.audience.label}</span>
            <span className="field__hint field__hint--lead">
              {copy.records.identity.audience.hint}
            </span>
            <textarea
              className="field__input"
              rows={3}
              maxLength={BRIEF_CAPS.target_audience}
              placeholder={copy.records.identity.audience.placeholder}
              value={form.who_its_for}
              disabled={!isOwner}
              onChange={(event) => setForm({ ...form, who_its_for: event.target.value })}
            />
          </label>
        </div>
        {!isOwner ? <p className="field__hint">Only the owner can change these two.</p> : null}

        <div className="kb-form__grid">
          <label className="field">
            <span className="field__label">{copy.records.identity.tone}</span>
            <input
              className="field__input"
              type="text"
              maxLength={200}
              placeholder={copy.records.identity.tonePlaceholder}
              value={form.tone}
              onChange={(event) => setForm({ ...form, tone: event.target.value })}
            />
          </label>

          {/* The meter sits outside the label so it never joins the field's
            accessible name, which would then change on every keystroke. */}
          <div className="field-stack">
            <label className="field">
              <span className="field__label">{copy.records.identity.tagline}</span>
              <input
                className="field__input"
                type="text"
                maxLength={200}
                placeholder="Roasted on the street it's sold on"
                value={form.tagline}
                onChange={(event) => setForm({ ...form, tagline: event.target.value })}
              />
            </label>
            {/* Taglines get appended to captions, so they carry the same limits. */}
            <CharMeter text={form.tagline} />
          </div>
        </div>

        <fieldset className="kb-fieldset">
          <legend className="field__label">Colors</legend>
          <div className="color-grid">
            {COLOR_KEYS.map((entry) => {
              const value = form.colors[entry.key] ?? '';
              return (
                <div className="color-field" key={entry.key}>
                  <span className="field__label">{entry.label}</span>
                  <span className="field__hint">{entry.hint}</span>
                  <div className="color-field__controls">
                    <input
                      className="color-field__swatch"
                      type="color"
                      aria-label={`${entry.label} color picker`}
                      value={HEX_COLOR.test(value) ? value : '#000000'}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          colors: { ...form.colors, [entry.key]: event.target.value },
                        })
                      }
                    />
                    <input
                      className="field__input color-field__hex"
                      type="text"
                      aria-label={`${entry.label} hex value`}
                      placeholder="#3A5A78"
                      maxLength={7}
                      value={value}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          colors: { ...form.colors, [entry.key]: event.target.value },
                        })
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="kb-fieldset">
          <legend className="field__label">Fonts</legend>
          <div className="kb-form__row">
            {FONT_KEYS.map((entry) => (
              <label className="field" key={entry.key}>
                <span className="field__label">{entry.label}</span>
                <input
                  className="field__input"
                  type="text"
                  maxLength={120}
                  placeholder="IBM Plex Sans"
                  value={form.fonts[entry.key] ?? ''}
                  onChange={(event) =>
                    setForm({ ...form, fonts: { ...form.fonts, [entry.key]: event.target.value } })
                  }
                />
                <span className="field__hint">{entry.hint}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <TokenListEditor
          label="Words to avoid"
          hint="Words the caption check blocks outright, before anything is made."
          placeholder="revolutionary"
          addLabel="Add term"
          tokens={form.never_say}
          onChange={(never_say) => setForm({ ...form, never_say })}
        />

        <PictureStylePrefs
          use={form.picture_styles}
          avoid={form.avoid_picture_styles}
          onChange={({ use, avoid }) =>
            setForm({ ...form, picture_styles: use, avoid_picture_styles: avoid })
          }
        />

        <div className="field-stack">
          <label className="field">
            <span className="field__label">Picture notes</span>
            <textarea
              className="field__input"
              rows={3}
              maxLength={PICTURE_NOTES_MAX}
              placeholder="Real people, bright daylight, never stock smiles."
              value={form.picture_notes}
              onChange={(event) => setForm({ ...form, picture_notes: event.target.value })}
            />
            <span className="field__hint">
              How should your pictures feel? We read this before we pick a style and a shot.
            </span>
          </label>
          <p className="layer-editor__meter">
            <span className={counterClassName(form.picture_notes.length, PICTURE_NOTES_MAX)}>
              {form.picture_notes.length}/{PICTURE_NOTES_MAX}
            </span>
            <span className="layer-editor__meter-label">characters</span>
          </p>
        </div>

        {saveError ? (
          <p className="form__error" role="alert">
            {saveError}
          </p>
        ) : null}

        <div className="kb-form__actions">
          <button className="button button--primary" type="submit" disabled={isSaving}>
            {isSaving ? 'Saving' : 'Save'}
          </button>
          {/* Earned mark: the record is on file, stamped with its real timestamp. */}
          {savedAt ? <QcStamp label="Saved" sub={formatTimestamp(savedAt)} /> : null}
        </div>
      </form>

      <div className="kb-subsection">
        <h3 className="kb-subsection__title">{copy.records.identity.logo}</h3>
        <p className="field__hint">
          PNG, JPEG, WebP or GIF, under 5 MB. Placement on slides uses preset positions only.
        </p>

        <div className="logo-field">
          {identity?.logo_path ? (
            <AssetPreview storagePath={identity.logo_path} variant="logo" />
          ) : (
            <div className="asset-preview asset-preview--logo">
              <span className="asset-preview__note">No logo yet</span>
            </div>
          )}

          <div className="logo-field__controls">
            <label className="button" aria-disabled={isUploading}>
              {isUploading ? 'Uploading' : identity?.logo_path ? 'Replace logo' : 'Upload logo'}
              <input
                className="visually-hidden"
                type="file"
                accept={IMAGE_ACCEPT_ATTRIBUTE}
                disabled={isUploading}
                onChange={(event) => void handleLogoChange(event)}
              />
            </label>
            {identity?.logo_path ? (
              <button
                className="button"
                type="button"
                disabled={isUploading}
                onClick={() => void handleLogoClear()}
              >
                Clear logo
              </button>
            ) : null}
          </div>
        </div>

        {logoError ? (
          <p className="form__error" role="alert">
            {logoError}
          </p>
        ) : null}
      </div>
    </>
  );
}

/**
 * Brand identity — the singleton knowledge-base record.
 *
 * What you do and who it is for (the business row's `value_proposition` and
 * `target_audience`, owner-only, saved by the same button through
 * `PUT /businesses/{id}` before the identity PUT), then tone, tagline, colors,
 * fonts, never-say list, picture preferences and logo.
 * Everything here feeds generation and rendering; the never-say list is
 * enforced by the deterministic copy guardrail, not by a model.
 *
 * `brand_identity.location` is not on this form, and is no longer edited
 * anywhere in the UI: the newsfeed's area filter, the one reader it had, was
 * taken off that screen. The column and the PUT are unchanged — whatever a
 * business has on file stays on file, and the server still tags items with it
 * — this form simply never sends the field.
 *
 * This wrapper does the load only. The loaded record is tagged with its
 * business and the visible state is derived during render, so the effect never
 * writes state synchronously and another business's brand can never show.
 *
 * When the record changes elsewhere (the assistant edited it, or an edit was
 * undone) it is re-read and the editor remounts on it; unsaved typing is not
 * lost, because the editor's draft autosave restores it on the remount.
 */
export function BrandIdentitySection({ businessId }: { businessId: string }) {
  const copy = useBrandCopy();
  const [loaded, setLoaded] = useState<LoadedIdentity | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);
  useRefreshOn(businessId, 'brand_identity', refresh);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const result = await fetchBrandIdentity(businessId, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId, identity: result, error: null, nonce });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        // The singleton may not exist yet; the first save creates it.
        if (caught instanceof ApiError && caught.status === 404) {
          setLoaded({ businessId, identity: null, error: null, nonce });
          return;
        }
        setLoaded((current) =>
          // A failed re-read keeps the form already on screen.
          current?.businessId === businessId && current.error === null
            ? current
            : {
                businessId,
                identity: null,
                error: getErrorMessage(caught, 'Could not load brand identity.'),
                nonce,
              },
        );
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, nonce]);

  const isCurrent = loaded !== null && loaded.businessId === businessId;

  return (
    <SectionShell
      title={copy.sections['brand-identity']}
      lede={copy.records.identity.lede}
      isLoading={!isCurrent}
      error={isCurrent ? loaded.error : null}
      isEmpty={false}
      emptyMessage=""
    >
      {isCurrent ? (
        <BrandIdentityEditor key={loaded.nonce} businessId={businessId} loaded={loaded.identity} />
      ) : null}
    </SectionShell>
  );
}
